import React, { useState, useEffect } from 'react'

const ProgramTitles = () => {
  const [activeTab, setActiveTab] = useState('program-1');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['program-1', 'program-2', 'program-3'];
      const scrollPosition = window.scrollY + 200;

      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveTab(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -30;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="programs-page-wrapper">
      {/* Sticky Program Quick Navigation Bar */}
      <div className="program-nav-sticky">
        <div className="program-nav-container">
          <button 
            className={`program-nav-btn ${activeTab === 'program-1' ? 'active' : ''}`}
            onClick={() => scrollToSection('program-1')}
          >
            <i className="fa-solid fa-hand-holding-heart"></i> Back2LIFE Reintegration
          </button>
          <button 
            className={`program-nav-btn ${activeTab === 'program-2' ? 'active' : ''}`}
            onClick={() => scrollToSection('program-2')}
          >
            <i className="fa-solid fa-shield-halved"></i> 1Life2Live Violence Prevention
          </button>
          <button 
            className={`program-nav-btn ${activeTab === 'program-3' ? 'active' : ''}`}
            onClick={() => scrollToSection('program-3')}
          >
            <i className="fa-solid fa-scale-balanced"></i> L.E.A.D. Diversion Program
          </button>
        </div>
      </div>

      <div className="programs-main-container">

        {/* PROGRAM 1: Back2LIFE Reintegration */}
        <section id="program-1" className="program-card-section">
          <div className="program-card-header">
            <div className="program-header-info">
              <span className="program-badge badge-red">Reintegration & Transition</span>
              <h2 className="program-title">Back2LIFE Reintegration & Transition Support Program</h2>
              <p className="program-subtitle">
                Back2LIFE collaborates directly with men and women residing at the Schenectady County Jail to reduce recidivism rates, foster stability, and bolster public safety across Schenectady County through a dedicated 6-member team.
              </p>
            </div>
            <div className="program-header-image">
              <img 
                src="https://images.unsplash.com/photo-1573497491208-6b1acb260507?q=80&w=2070&auto=format&fit=crop" 
                alt="Back2LIFE Reintegration Team Support" 
              />
            </div>
          </div>

          <div className="program-roles-grid">
            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-clipboard-check"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Initial Assessment</span>
                <h4>Intake Assessment & Coordinator</h4>
                <p>
                  Conducts comprehensive assessments for individuals staying in the facility over seven days to understand the circumstances surrounding arrest and detention, identify challenges, and create tailored plans for life post-release.
                </p>
              </div>
            </div>

            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-hands-holding-child"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">In-Facility Services</span>
                <h4>Inmate Services & Programs Coordinator</h4>
                <p>
                  Ensures individuals retain essential access to families, legal representation, and court proceedings. Collaborates with community volunteers for skill classes, vocational training, and spiritual guidance.
                </p>
              </div>
            </div>

            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-map-location-dot"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Discharge Strategy</span>
                <h4>Discharge Coordinator</h4>
                <p>
                  Meets with sentenced individuals and those scheduled for release to craft individualized transition plans. Addresses housing, employment, education, healthcare, and family connections to reduce recidivism risk.
                </p>
              </div>
            </div>

            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-user-group"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Ongoing Coaching</span>
                <h4>Community Transition & Reintegration Coach</h4>
                <p>
                  Initiates post-discharge arrangements for mental health, substance use, DSS, and job placement. Follows up with in-person and phone check-ins after release to maintain a strong support network.
                </p>
              </div>
            </div>

            <div className="role-card highlight-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-heart-pulse"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Mental Health & Wellness</span>
                <h4>Tier I & Tier II Counseling Support</h4>
                <p>
                  Provides specialized counseling during confinement and continuing after release. <strong>Tier I</strong> addresses situational stress and relationship support; <strong>Tier II</strong> provides deep clinical support for depression, loss, and trauma.
                </p>
              </div>
            </div>
          </div>
        </section>


        {/* PROGRAM 2: 1Life2Live Gun & Gang Violence Prevention */}
        <section id="program-2" className="program-card-section">
          <div className="program-card-header">
            <div className="program-header-info">
              <span className="program-badge badge-darkred">Violence Prevention & Intervention</span>
              <h2 className="program-title">1Life2Live Gun & Gang Violence Prevention & Intervention</h2>
              <p className="program-subtitle">
                A dedicated community-driven initiative aimed at decreasing shots fired, shooting injuries, and homicides across Schenectady County through proactive street mediation and high-risk participant coaching.
              </p>
            </div>
            <div className="program-header-image">
              <img 
                src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=2070&auto=format&fit=crop" 
                alt="Community Outreach & Violence Prevention" 
              />
            </div>
          </div>

          <div className="program-roles-grid 3-col">
            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-users-gear"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Credible Messengers</span>
                <h4>Street Team Outreach & Caseloads</h4>
                <p>
                  Comprised of men and women with deep street credibility and relationships. Each member manages a caseload of 8 high-risk participants, connecting them with employment, trade training, and positive behavior redirection.
                </p>
              </div>
            </div>

            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-truck-medical"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Crisis De-escalation</span>
                <h4>Hospital & Incident Rapid Response</h4>
                <p>
                  Responds immediately to shooting incidents and hospital emergency rooms to calm high emotions, mediate underlying conflicts with involved parties, and prevent retaliatory violence.
                </p>
              </div>
            </div>

            <div className="role-card">
              <div className="role-card-icon">
                <i className="fa-solid fa-chart-line"></i>
              </div>
              <div className="role-card-content">
                <span className="role-tag">Expansion</span>
                <h4>Community Team Growth</h4>
                <p>
                  Continuously expanding team coverage across high-impact neighborhoods to strengthen safety nets and provide robust wrap-around support for program participants.
                </p>
              </div>
            </div>
          </div>

          {/* Confidentiality Callout */}
          <div className="program-confidential-callout">
            <div className="callout-icon">
              <i className="fa-solid fa-user-shield"></i>
            </div>
            <div className="callout-text">
              <h4>Strict Confidentiality & Trust Promise</h4>
              <p>
                The success of the Street Team hinges on trust and credibility. Information gathered by the Street Team remains <strong>100% confidential</strong> and is <strong>never shared with law enforcement or the criminal justice system</strong>. Clear boundaries are strictly honored by all stakeholders.
              </p>
            </div>
          </div>
        </section>


        {/* PROGRAM 3: L.E.A.D. Diversion Program */}
        <section id="program-3" className="program-card-section">
          <div className="program-card-header">
            <div className="program-header-info">
              <span className="program-badge badge-red">Pre-Arrest Harm Reduction</span>
              <h2 className="program-title">L.I.F.E.’s Alternative: L.E.A.D Diversion Program</h2>
              <p className="program-subtitle">
                Let Everyone Advance with Dignity (L.E.A.D.) enables law enforcement and community partners to refer individuals facing mental health, poverty, or homelessness challenges to support services rather than the criminal justice system.
              </p>
            </div>
            <div className="program-header-image">
              <img 
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070&auto=format&fit=crop" 
                alt="Community Leadership & Advancement" 
              />
            </div>
          </div>

          <div className="lead-two-column-layout">
            {/* Column 1: Referral Pathways */}
            <div className="lead-column">
              <div className="lead-column-header">
                <i className="fa-solid fa-route"></i>
                <h3>Referral Pathways</h3>
              </div>
              <div className="lead-card-stack">
                <div className="lead-card">
                  <h4><i className="fa-solid fa-users"></i> Social Contact Referrals</h4>
                  <p>
                    Community partners and stakeholders suggest individuals meeting program criteria. After website referral approval by the Project Manager, our Intake Coordinator guides enrollment without law enforcement involvement.
                  </p>
                </div>
                <div className="lead-card">
                  <h4><i className="fa-solid fa-house-user"></i> Community Contact Referrals</h4>
                  <p>
                    Neighbors, family members, or local community members recommend individuals who could benefit from support, opening direct pathways for enrollment.
                  </p>
                </div>
                <div className="lead-card">
                  <h4><i className="fa-solid fa-user-check"></i> Self-Referrals</h4>
                  <p>
                    Individuals can self-refer directly to program staff. Case Managers meet with them immediately to complete paperwork and connect them with vital services.
                  </p>
                </div>
              </div>
            </div>

            {/* Column 2: Program Governance & Pillars */}
            <div className="lead-column">
              <div className="lead-column-header">
                <i className="fa-solid fa-building-columns"></i>
                <h3>Core Governance Pillars</h3>
              </div>
              <div className="lead-card-stack">
                <div className="lead-card">
                  <h4><i className="fa-solid fa-gavel"></i> Policy Coordinating Group (PCG)</h4>
                  <p>
                    Leadership representatives from Sheriff’s Office, Police Dept, District Attorney, Public Defender, Health Dept, DSS, Galesi Group, Melozzi Group, and YLSN convening monthly to eliminate systemic barriers.
                  </p>
                </div>
                <div className="lead-card">
                  <h4><i className="fa-solid fa-list-check"></i> Operation Work Group (OWG)</h4>
                  <p>
                    Conducts bi-weekly case reviews for program enrollees to monitor progress, optimize individual outcomes, and propose policy enhancements.
                  </p>
                </div>
                <div className="lead-card">
                  <h4><i className="fa-solid fa-hands-holding"></i> Community Leadership Team</h4>
                  <p>
                    Managed by Community Engagement Coordinators to build an active network of faith groups, businesses, health providers, and neighborhood associations to support participants.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  )
}

export default ProgramTitles